import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type {
  AuditLog,
  FeatureFlag,
  Invite,
  MembershipDetail,
  TenantDetail,
  TenantListItem,
  TenantPlan,
  TenantStatus,
} from "../types/admin";
import type { AppRole } from "../types/auth";
import type { Profile } from "../types/tenant";
import type { TenantMembershipWithTenant } from "../types/tenant";

const ROLE_PRIORITY: AppRole[] = [
  "platform_admin",
  "tenant_owner",
  "trainer",
  "assistant",
  "client",
];

export const tenantService = {
  async getMembershipsForUser(
    userId: string,
  ): Promise<TenantMembershipWithTenant[]> {
    assertSupabaseConfigured();

    const { data, error } = await supabase
      .from("tenant_memberships")
      .select("*, tenants(*)")
      .eq("user_id", userId)
      .eq("status", "active")
      .order("created_at", { ascending: true });

    if (error) {
      throw error;
    }

    return (data ?? []) as TenantMembershipWithTenant[];
  },

  async listTenants(search = "", limit = 100): Promise<TenantListItem[]> {
    assertSupabaseConfigured();

    let query = supabase
      .from("tenants")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (search.trim()) {
      query = query.or(`name.ilike.%${search.trim()}%,slug.ilike.%${search.trim()}%`);
    }

    const { data: tenants, error } = await query;

    if (error) {
      throw error;
    }

    const tenantRows = (tenants ?? []) as TenantListItem[];
    const tenantIds = tenantRows.map((tenant) => tenant.id);
    const ownerIds = tenantRows
      .map((tenant) => tenant.owner_user_id)
      .filter((value): value is string => Boolean(value));

    const [{ data: memberships }, { data: owners }] = await Promise.all([
      tenantIds.length
        ? supabase
            .from("tenant_memberships")
            .select("tenant_id")
            .in("tenant_id", tenantIds)
        : Promise.resolve({ data: [] }),
      ownerIds.length
        ? supabase.from("profiles").select("id,email,full_name").in("id", ownerIds)
        : Promise.resolve({ data: [] }),
    ]);

    const counts = new Map<string, number>();
    for (const membership of memberships ?? []) {
      if (membership.tenant_id) {
        counts.set(membership.tenant_id, (counts.get(membership.tenant_id) ?? 0) + 1);
      }
    }

    const ownerMap = new Map(
      ((owners ?? []) as Pick<Profile, "id" | "email" | "full_name">[]).map(
        (owner) => [owner.id, owner],
      ),
    );

    return tenantRows.map((tenant) => ({
      ...tenant,
      member_count: counts.get(tenant.id) ?? 0,
      profiles: tenant.owner_user_id ? ownerMap.get(tenant.owner_user_id) ?? null : null,
    }));
  },

  async getTenantDetail(tenantId: string): Promise<{
    tenant: TenantDetail | null;
    members: MembershipDetail[];
    invites: Invite[];
    featureFlags: FeatureFlag[];
    auditLogs: AuditLog[];
  }> {
    assertSupabaseConfigured();

    const [tenantResult, membersResult, invitesResult, flagsResult, logsResult] =
      await Promise.all([
        supabase.from("tenants").select("*").eq("id", tenantId).maybeSingle(),
        supabase
          .from("tenant_memberships")
          .select("*, tenants(*), profiles(id,email,full_name,status)")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false }),
        supabase
          .from("invites")
          .select("*, tenants(id,name,slug,status,plan)")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false }),
        supabase
          .from("feature_flags")
          .select("*, tenants(id,name,slug)")
          .eq("tenant_id", tenantId)
          .order("key", { ascending: true }),
        supabase
          .from("audit_logs")
          .select("*, tenants(id,name,slug), profiles(id,email,full_name)")
          .eq("tenant_id", tenantId)
          .order("created_at", { ascending: false })
          .limit(50),
      ]);

    if (tenantResult.error) throw tenantResult.error;
    if (membersResult.error) throw membersResult.error;
    if (invitesResult.error) throw invitesResult.error;
    if (flagsResult.error) throw flagsResult.error;
    if (logsResult.error) throw logsResult.error;

    let owner: Pick<Profile, "id" | "email" | "full_name" | "status"> | null =
      null;

    if (tenantResult.data?.owner_user_id) {
      const { data, error } = await supabase
        .from("profiles")
        .select("id,email,full_name,status")
        .eq("id", tenantResult.data.owner_user_id)
        .maybeSingle();

      if (error) throw error;
      owner = data;
    }

    return {
      tenant: tenantResult.data
        ? ({ ...tenantResult.data, profiles: owner } as TenantDetail)
        : null,
      members: (membersResult.data ?? []) as MembershipDetail[],
      invites: (invitesResult.data ?? []) as Invite[],
      featureFlags: (flagsResult.data ?? []) as FeatureFlag[],
      auditLogs: (logsResult.data ?? []) as AuditLog[],
    };
  },

  async updateTenant(input: {
    tenantId: string;
    status?: TenantStatus | null;
    plan?: TenantPlan | null;
    ownerUserId?: string | null;
  }) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_update_tenant", {
      p_tenant_id: input.tenantId,
      p_status: input.status ?? null,
      p_plan: input.plan ?? null,
      p_owner_user_id: input.ownerUserId ?? null,
    });

    if (error) {
      throw error;
    }
  },

  async extendTrial(tenantId: string, days = 14) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_extend_tenant_trial", {
      p_tenant_id: tenantId,
      p_days: days,
    });

    if (error) {
      throw error;
    }
  },

  async convertTrial(tenantId: string, plan: TenantPlan) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_convert_tenant_trial", {
      p_tenant_id: tenantId,
      p_plan: plan,
    });

    if (error) {
      throw error;
    }
  },

  async deleteTenant(tenantId: string, confirmSlug: string) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_delete_tenant", {
      p_tenant_id: tenantId,
      p_confirm_slug: confirmSlug,
    });

    if (error) {
      throw error;
    }
  },

  async updateMembership(input: {
    membershipId: string;
    role?: AppRole | null;
    status?: "active" | "invited" | "disabled" | null;
  }) {
    assertSupabaseConfigured();

    const { error } = await supabase.rpc("admin_update_membership", {
      p_membership_id: input.membershipId,
      p_role: input.role ?? null,
      p_status: input.status ?? null,
    });

    if (error) {
      throw error;
    }
  },

  resolvePrimaryMembership(memberships: TenantMembershipWithTenant[]) {
    const activeMemberships = memberships.filter(
      (membership) => membership.status === "active",
    );

    return (
      [...activeMemberships].sort(
        (a, b) =>
          ROLE_PRIORITY.indexOf(a.role as AppRole) -
          ROLE_PRIORITY.indexOf(b.role as AppRole),
      )[0] ?? null
    );
  },
};
